<template>
  <div class="p-4 md:p-6" dir="rtl">
    <div class="flex items-center justify-between mb-4">
      <div>
        <h2 class="text-xl font-semibold text-gray-800">البلاغات / الشكاوى / الاقتراحات</h2>
        <p class="text-sm text-gray-500">
          يمكنك فتح بلاغ ومتابعة حالته.
        </p>
      </div>
      <button
        class="px-4 py-2 rounded-md bg-primary text-white hover:bg-primary/90"
        @click="openCreate = !openCreate"
      >
        {{ openCreate ? 'إغلاق' : 'فتح بلاغ جديد' }}
      </button>
    </div>

    <!-- Create -->
    <div v-if="openCreate" class="bg-white rounded-lg shadow p-4 mb-6 border border-gray-100">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label class="block text-sm text-gray-600 mb-1">نوع البلاغ</label>
          <select v-model="createForm.ticketType" class="w-full border rounded-md p-2">
            <option value="COMPLAINT">شكوى</option>
            <option value="SUGGESTION">اقتراح</option>
            <option value="REPORT">بلاغ</option>
            <option value="OTHER">أخرى</option>
          </select>
        </div>
        <div class="md:col-span-2">
          <label class="block text-sm text-gray-600 mb-1">العنوان</label>
          <input v-model="createForm.title" class="w-full border rounded-md p-2" placeholder="عنوان مختصر" />
        </div>
        <div class="md:col-span-3">
          <label class="block text-sm text-gray-600 mb-1">التفاصيل</label>
          <textarea v-model="createForm.description" class="w-full border rounded-md p-2" rows="4" placeholder="اكتب تفاصيل البلاغ..."></textarea>
        </div>
        <div class="md:col-span-3">
          <label class="block text-sm text-gray-600 mb-1">صورة (اختياري)</label>
          <input
            type="file"
            accept="image/*"
            class="w-full border rounded-md p-2 bg-white"
            @change="(e) => (createImage = e.target.files && e.target.files[0] ? e.target.files[0] : null)"
          />
          <p v-if="createImage" class="text-xs text-gray-500 mt-1">تم اختيار: {{ createImage.name }}</p>
        </div>
      </div>
      <div class="mt-3 flex items-center gap-3">
        <button
          class="px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
          :disabled="loadingCreate"
          @click="submitCreate"
        >
          {{ loadingCreate ? 'جارٍ الإرسال...' : 'إرسال' }}
        </button>
        <p v-if="createError" class="text-sm text-red-600">{{ createError }}</p>
      </div>
    </div>

    <!-- Tabs -->
    <div class="flex items-center gap-2 mb-4">
      <button
        class="px-3 py-2 rounded-md border"
        :class="tab === 'my' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200'"
        @click="tab = 'my'; refresh()"
      >
        بلاغاتي
      </button>
      <button
        v-if="canSeeAllTickets"
        class="px-3 py-2 rounded-md border"
        :class="tab === 'inbox' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200'"
        @click="tab = 'inbox'; refresh()"
      >
        كل البلاغات (كل الفروع)
      </button>
      <div class="flex-1"></div>
      <button class="px-3 py-2 rounded-md border border-gray-200 bg-white" @click="refresh" :disabled="loadingList">
        تحديث
      </button>
    </div>

    <!-- List -->
    <div class="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
      <div v-if="loadingList" class="p-6 text-center text-gray-500">جارٍ التحميل...</div>
      <div v-else>
        <div v-if="!tickets.length" class="p-6 text-center text-gray-500">لا يوجد بلاغات</div>
        <table v-else class="w-full text-sm">
          <thead class="bg-gray-50 text-gray-600">
            <tr>
              <th class="text-right p-3">#</th>
              <th class="text-right p-3">العنوان</th>
              <th class="text-right p-3">النوع</th>
              <th class="text-right p-3">الحالة</th>
              <th class="text-right p-3">آخر تحديث</th>
              <th class="text-right p-3"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="t in tickets" :key="t.id" class="border-t">
              <td class="p-3 text-gray-700">{{ t.id }}</td>
              <td class="p-3 text-gray-800 font-medium">{{ t.title }}</td>
              <td class="p-3 text-gray-600">{{ mapType(t.ticketType) }}</td>
              <td class="p-3">
                <span class="px-2 py-1 rounded-full text-xs" :class="statusClass(t.status)">
                  {{ mapStatus(t.status) }}
                </span>
              </td>
              <td class="p-3 text-gray-600">{{ formatDate(t.lastActivityAt || t.createdAt) }}</td>
              <td class="p-3 text-left">
                <button class="px-3 py-1 rounded-md border border-gray-200 hover:bg-gray-50" @click="openDetails(t.id)">
                  عرض
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Details modal -->
    <div v-if="detailsOpen" class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="absolute inset-0 bg-black/50" @click="closeDetails"></div>
      <div class="relative bg-white w-full max-w-3xl mx-4 rounded-lg shadow-lg overflow-hidden">
        <div class="p-4 border-b flex items-center justify-between">
          <div>
            <div class="text-sm text-gray-500">بلاغ رقم {{ details?.id }}</div>
            <div class="text-lg font-semibold text-gray-800">{{ details?.title }}</div>
          </div>
          <button class="px-3 py-1 rounded-md border border-gray-200 hover:bg-gray-50" @click="closeDetails">إغلاق</button>
        </div>

        <div class="p-4">
          <div class="flex flex-wrap items-center gap-3 mb-3">
            <span class="px-2 py-1 rounded-full text-xs" :class="statusClass(details?.status)">
              {{ mapStatus(details?.status) }}
            </span>
            <span class="text-sm text-gray-600">النوع: {{ mapType(details?.ticketType) }}</span>
            <span class="text-sm text-gray-600">المصدر: {{ details?.source === 'ADMIN_PANEL' ? 'لوحة الإدارة' : 'التطبيق' }}</span>
            <span class="text-sm text-gray-600">آخر تحديث: {{ formatDate(details?.lastActivityAt) }}</span>
          </div>

          <div class="bg-gray-50 p-3 rounded-md mb-4 text-gray-700 whitespace-pre-wrap">
            {{ details?.description }}
          </div>

          <div class="mb-4">
            <div class="text-sm font-semibold text-gray-700 mb-2">المحادثة</div>
            <div class="space-y-2 max-h-64 overflow-auto border rounded-md p-3">
              <div v-for="m in (details?.messages || [])" :key="m.id" class="p-2 rounded-md border border-gray-100">
                <div class="flex items-center justify-between text-xs text-gray-500 mb-1">
                  <span>{{ m.senderName || ('مستخدم #' + m.senderUserId) }} ({{ m.senderRoleCode || '-' }})</span>
                  <span>{{ formatDate(m.createdAt) }}</span>
                </div>
                <div class="text-sm text-gray-800 whitespace-pre-wrap">{{ m.message }}</div>
            <div v-if="m.attachmentUrl" class="mt-2">
              <a :href="m.attachmentUrl" target="_blank" class="text-xs text-primary underline">
                {{ m.attachmentName || 'عرض الصورة' }}
              </a>
              <div class="mt-2">
                <img :src="m.attachmentUrl" class="max-h-48 rounded-md border" />
              </div>
            </div>
              </div>
            </div>
          </div>

      <div v-if="!isUpperManagement" class="border-t pt-4">
        <div class="text-sm font-semibold text-gray-700 mb-2">إضافة رسالة</div>
        <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div class="md:col-span-3">
            <textarea
              v-model="messageForm.message"
              class="w-full border rounded-md p-2"
              rows="3"
              placeholder="اكتب رسالة متابعة..."
            ></textarea>
          </div>
          <div>
            <label class="block text-sm text-gray-600 mb-1">صورة (اختياري)</label>
            <input
              type="file"
              accept="image/*"
              class="w-full border rounded-md p-2 bg-white"
              @change="(e) => (messageImage = e.target.files && e.target.files[0] ? e.target.files[0] : null)"
            />
            <p v-if="messageImage" class="text-xs text-gray-500 mt-1">تم اختيار: {{ messageImage.name }}</p>
          </div>
        </div>
        <div class="mt-3 flex items-center gap-3">
          <button
            class="px-4 py-2 rounded-md bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
            :disabled="loadingMessage"
            @click="sendMessage"
          >
            {{ loadingMessage ? 'جارٍ الإرسال...' : 'إرسال' }}
          </button>
          <p v-if="messageError" class="text-sm text-red-600">{{ messageError }}</p>
        </div>
      </div>

          <div v-if="isUpperManagement" class="border-t pt-4">
            <div class="text-sm font-semibold text-gray-700 mb-2">رد الإدارة العليا</div>
            <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div class="md:col-span-3">
                <textarea v-model="replyForm.message" class="w-full border rounded-md p-2" rows="3" placeholder="اكتب الرد..."></textarea>
              </div>
              <div>
                <label class="block text-sm text-gray-600 mb-1">تغيير الحالة (اختياري)</label>
                <select v-model="replyForm.newStatus" class="w-full border rounded-md p-2">
                  <option value="">بدون تغيير</option>
                  <option value="OPEN">مفتوح</option>
                  <option value="IN_PROGRESS">قيد المعالجة</option>
                  <option value="ANSWERED">تم الرد</option>
                  <option value="CLOSED">مغلق</option>
                </select>
              </div>
            </div>
        <div class="mt-3">
          <label class="block text-sm text-gray-600 mb-1">صورة (اختياري)</label>
          <input
            type="file"
            accept="image/*"
            class="w-full border rounded-md p-2 bg-white"
            @change="(e) => (replyImage = e.target.files && e.target.files[0] ? e.target.files[0] : null)"
          />
          <p v-if="replyImage" class="text-xs text-gray-500 mt-1">تم اختيار: {{ replyImage.name }}</p>
        </div>
            <div class="mt-3 flex items-center gap-3">
              <button
                class="px-4 py-2 rounded-md bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
                :disabled="loadingReply"
                @click="sendReply"
              >
                {{ loadingReply ? 'جارٍ الإرسال...' : 'إرسال الرد' }}
              </button>
              <p v-if="replyError" class="text-sm text-red-600">{{ replyError }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  createSupportTicket,
  getMySupportTickets,
  getSupportTicketDetails,
  adminListSupportTickets,
  adminReplySupportTicket,
  addSupportTicketMessage,
} from '@/services/supportTickets';

export default {
  name: 'SupportTickets',
  data() {
    const role = localStorage.getItem('userRole') || '';
    return {
      tab: 'my',
      role,
      openCreate: false,
      loadingCreate: false,
      createError: '',
      createForm: {
        ticketType: 'COMPLAINT',
        title: '',
        description: '',
        source: 'ADMIN_PANEL',
      },
      createImage: null,
      loadingList: false,
      tickets: [],
      detailsOpen: false,
      details: null,
      loadingDetails: false,
      replyForm: { message: '', newStatus: '' },
      replyImage: null,
      loadingReply: false,
      replyError: '',
      messageForm: { message: '' },
      messageImage: null,
      loadingMessage: false,
      messageError: '',
    };
  },
  computed: {
    isUpperManagement() {
      return this.role === 'GENERAL_MANAGER' || this.role === 'SUPER_ADMIN';
    },
    isAdmin() {
      return this.role === 'ADMIN';
    },
    canSeeAllTickets() {
      return this.isUpperManagement || this.isAdmin;
    },
  },
  mounted() {
    // Default tab for roles that can see all: inbox
    if (this.canSeeAllTickets) {
      this.tab = 'inbox';
    }
    this.refresh();
  },
  methods: {
    mapType(type) {
      const map = {
        COMPLAINT: 'شكوى',
        SUGGESTION: 'اقتراح',
        REPORT: 'بلاغ',
        OTHER: 'أخرى',
      };
      return map[type] || type || '-';
    },
    mapStatus(status) {
      const map = {
        OPEN: 'مفتوح',
        IN_PROGRESS: 'قيد المعالجة',
        ANSWERED: 'تم الرد',
        CLOSED: 'مغلق',
      };
      return map[status] || status || '-';
    },
    statusClass(status) {
      switch (status) {
        case 'OPEN':
          return 'bg-blue-50 text-blue-700';
        case 'IN_PROGRESS':
          return 'bg-yellow-50 text-yellow-800';
        case 'ANSWERED':
          return 'bg-green-50 text-green-700';
        case 'CLOSED':
          return 'bg-gray-100 text-gray-700';
        default:
          return 'bg-gray-100 text-gray-700';
      }
    },
    formatDate(iso) {
      if (!iso) return '-';
      try {
        const d = new Date(iso);
        return d.toLocaleString('ar-SA');
      } catch (e) {
        return iso;
      }
    },
    async refresh() {
      this.loadingList = true;
      try {
        const res =
          this.tab === 'inbox'
            ? await adminListSupportTickets({ page: 0, size: 50 })
            : await getMySupportTickets({ page: 0, size: 50 });
        this.tickets = res?.content || [];
      } catch (e) {
        console.error('Support tickets list error:', e?.response?.data || e);
        this.tickets = [];
      } finally {
        this.loadingList = false;
      }
    },
    async submitCreate() {
      this.createError = '';
      if (!this.createForm.title || !this.createForm.description) {
        this.createError = 'الرجاء إدخال العنوان والتفاصيل';
        return;
      }
      this.loadingCreate = true;
      try {
        await createSupportTicket({ ...this.createForm, image: this.createImage });
        this.createForm.title = '';
        this.createForm.description = '';
        this.createImage = null;
        this.openCreate = false;
        this.tab = 'my';
        await this.refresh();
      } catch (e) {
        console.error('Create ticket error:', e?.response?.data || e);
        this.createError = 'فشل إنشاء البلاغ';
      } finally {
        this.loadingCreate = false;
      }
    },
    async openDetails(ticketId) {
      this.detailsOpen = true;
      this.details = null;
      this.replyForm = { message: '', newStatus: '' };
      this.replyImage = null;
      this.replyError = '';
      this.messageForm = { message: '' };
      this.messageImage = null;
      this.messageError = '';
      this.loadingDetails = true;
      try {
        this.details = await getSupportTicketDetails(ticketId);
      } catch (e) {
        console.error('Ticket details error:', e?.response?.data || e);
        this.details = null;
      } finally {
        this.loadingDetails = false;
      }
    },
    closeDetails() {
      this.detailsOpen = false;
      this.details = null;
    },
    async sendReply() {
      this.replyError = '';
      if (!this.details?.id) return;
      if (!this.replyForm.message) {
        this.replyError = 'الرجاء كتابة الرد';
        return;
      }
      this.loadingReply = true;
      try {
        await adminReplySupportTicket(this.details.id, { ...this.replyForm, image: this.replyImage });
        // Refresh details + list
        this.details = await getSupportTicketDetails(this.details.id);
        await this.refresh();
        this.replyForm = { message: '', newStatus: '' };
        this.replyImage = null;
      } catch (e) {
        console.error('Reply error:', e?.response?.data || e);
        this.replyError = 'فشل إرسال الرد';
      } finally {
        this.loadingReply = false;
      }
    },

    async sendMessage() {
      this.messageError = '';
      if (!this.details?.id) return;
      if (!this.messageForm.message) {
        this.messageError = 'الرجاء كتابة الرسالة';
        return;
      }
      this.loadingMessage = true;
      try {
        await addSupportTicketMessage(this.details.id, { message: this.messageForm.message, image: this.messageImage });
        this.details = await getSupportTicketDetails(this.details.id);
        await this.refresh();
        this.messageForm = { message: '' };
        this.messageImage = null;
      } catch (e) {
        console.error('Send message error:', e?.response?.data || e);
        this.messageError = 'فشل إرسال الرسالة';
      } finally {
        this.loadingMessage = false;
      }
    },
  },
};
</script>


